import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { get, pageContext, getRulesFromState, rulesCache } from 'ctm-util-functions';

import { Anchor } from '../anchor/anchor';
import { SimpleFlexbox } from '../flexbox/flexbox';
import { Text } from '../text/text';
import { Icon } from '../icon/icon';
import styles from './phone_number.css';
import { getContent } from '../utils';

/**
 *  Wrapper component to pass in props to child text components to display various variations of the phone number
 * @returns {PhoneNumber} The phone number with a prefix
 */
class _PhoneNumber extends Component {

    constructor(props) {
        super(props);

        // set up the component variant builders
        this.builders = {
            default: this.buildHeaderVariant,
            header: this.buildHeaderVariant,
            callUsNow: this.buildCallUsNowVariant,
            modalMenuItem: this.buildCallUsNowVariant,
            simple: this.buildSimpleVariant,
            icon: this.buildIconVariant
        };
    }

    /**
     * Don't update if there are no rules, or if the rules result hasn't changed
     * @param {Object} nextProps - The incoming set of props
     * @param {Object} nestState - The incoming component state
     * @returns {Boolean} - True if the component should update
     */
    shouldComponentUpdate = (nextProps, nestState) => {
        const { rules } = this.props;
        return !!rules && get(this.props, 'rules.content.result') !== get(nextProps, 'rules.content.result');
    };

    /**
     *  builds the correct component variant based on visualProps.variant string
     *  also accepts an anchor element component which is identical on both variant types
     * @param {Node} anchorElement - the phone number tel link
     * @returns {Node} componentVariant - the component variant
     */
    buildVariant = (anchorElement) => {
        // store the variant name as a key
        const variant = get(this.props, 'visualProps.variant', 'default');
        // run and return the builder
        return this.builders[variant](anchorElement);
    };

    /**
     * construct and return the header variant of the component
     * @param {Node} anchorElement - the phone number tel link
     * @returns {Node} componentVariant - the component variant
     */
    buildHeaderVariant = (anchorElement) => {
        const { contentProps } = this.props;
        const { labelPrefix, supplementaryText } = contentProps;
        return (
            <SimpleFlexbox smOffset={4} mdOffset={5} lgOffset={6}>
                {
                    supplementaryText
                    && <Text tagName="div" label={supplementaryText} size="small" textAlign="center" />
                }
                {
                    labelPrefix
                    && <Text label={`${labelPrefix} `} size="xlarge" />
                }
                {anchorElement}
            </SimpleFlexbox>
        );
    };

    /**
     * construct and return the call us now, modal variant of the component
     * @param {Node} anchorElement - the phone number tel link
     * @returns {Node} componentVariant - the component variant
     */
    buildCallUsNowVariant = (anchorElement) => {
        const { visualProps } = this.props;
        const iconProps = { ...visualProps };
        delete iconProps.variant;
        return (
            <div className={styles[visualProps.variant]}>
                <Icon {...iconProps} variant="ctm_phone_modal" />
                {anchorElement}
            </div>
        );
    };

    /**
     * Nothing else
     * @param {Node} anchorElement - the phone number tel link
     * @returns {Node} componentVariant - the component variant
     */
    buildSimpleVariant = (anchorElement) => {
        return anchorElement;
    };

    buildIconVariant = () => {
        const { contentProps, elementProps, visualProps } = this.props;
        const iconProps = { ...visualProps };
        delete iconProps.variant;
        const { breakpoint } = pageContext.getProps().pageState;
        const anchorBreakpoints = ['xs', 'sm', 'md'];
        // For Anchor generate href if not provided
        if (anchorBreakpoints.includes(breakpoint) && (!elementProps || !elementProps.href)) {
            const getContentProps = { ...this.props, stateKey: contentProps.stateKey };
            const href = `tel:${getContent(getContentProps)}`;
            return <Anchor href={href}><Icon {...iconProps} variant="ctm_phone" /></Anchor>;
        }
        return <Icon {...iconProps} variant="ctm_phone" />;
    };

    /**
     * Render a PhoneNumber component
     * @returns {Node} The PhoneNumber component
     */
    render = () => {
        let ComponentType = Anchor;
        const { breakpoint } = pageContext.getProps().pageState;
        const anchorBreakpoints = ['xs', 'sm', 'md'];
        if (!anchorBreakpoints.includes(breakpoint)) {
            ComponentType = Text;
        }

        const { visualProps, contentProps, stateProps, children } = this.props;
        let { elementProps } = this.props;
        // For Anchor generate href if not provided
        if (anchorBreakpoints.includes(breakpoint) && (!elementProps || !elementProps.href)) {
            const getContentProps = { ...this.props, stateKey: contentProps.stateKey };
            elementProps = {
                href: `tel:${getContent(getContentProps)}`
            };
        }
        const element = (
            <ComponentType {...visualProps} {...contentProps} {...elementProps} stateProps={stateProps} variant="white_space_nowrap" data-id="phoneNumberLink">
                {children}
            </ComponentType>
        );

        return this.buildVariant(element);
    };

}

_PhoneNumber.propTypes = {
    visualProps: PropTypes.shape({
        variant: PropTypes.string,
        color: PropTypes.string,
        size: PropTypes.string
    }),
    contentProps: PropTypes.shape({
        /**
         * If a state key is provided, it will not use label or innerHTML
         */
        stateKey: PropTypes.string,
        label: PropTypes.string,
        icon: PropTypes.string,
        labelPrefix: PropTypes.string,
        supplementaryText: PropTypes.string
    }),
    elementProps: PropTypes.shape({
        href: PropTypes.string,
        target: PropTypes.string
    }),
    /**
     * stateProps Property so we can extract text from
     * @todo: not sure what to do with this, seems to be the only shaped prop we need,
     * as we need to extract the value from it based on stateKey
     */
    stateProps: PropTypes.object,
    children: PropTypes.node,
    // eslint-disable-next-line react/no-unused-prop-types
    rules: PropTypes.object
};

const mapStateToProps = (state, props) => {
    const rules = getRulesFromState(state, props);
    if (!Object.keys(rules).length) {
        return props;
    }

    let newNumber = null;
    const elementProps = { ...props.elementProps };
    const contentProps = { ...props.contentProps };
    if (get(rules, 'content.result', false)) {
        const elementRule = rulesCache.getElementRuleForId(rules.content.id);
        newNumber = elementRule.props.resultTrue;
        elementProps.href = `tel:${newNumber.split(' ').join('-')}`; // use replace here
        contentProps.label = newNumber;
    }

    return {
        elementProps,
        contentProps,
        rules
    };
};

const PhoneNumber = connect(mapStateToProps)(_PhoneNumber);

export { _PhoneNumber, PhoneNumber };
